import { useState } from 'react';
import { useStripe } from '@stripe/react-stripe-js';
import type { StripeCardElement } from '@stripe/stripe-js';

type BillingDetails = {
  name: string;
  address: {
    line1: string;
    city: string;
    state: string;
    postal_code: string;
    country: string;
  };
};

type PaymentMethodData = {
  type: 'card';
  card: StripeCardElement;
  billing_details: BillingDetails;
};

type StripePaymentMethod = {
  id: string;
  object: 'payment_method';
  type: string;
  card: {
    brand: string;
    last4: string;
    exp_month: number;
    exp_year: number;
  };
  billing_details: BillingDetails;
  created: number;
};

type UsePaymentMethodReturn = {
  createPaymentMethod: (paymentMethodData: PaymentMethodData) => Promise<StripePaymentMethod | null>;
  loading: boolean;
  error: string | null;
  paymentMethod: StripePaymentMethod | null;
};

 const usePaymentMethod = (): UsePaymentMethodReturn => {
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [paymentMethod, setPaymentMethod] = useState<StripePaymentMethod | null>(null);
  const stripe = useStripe();

  

  const createPaymentMethod = async (paymentMethodData: PaymentMethodData): Promise<StripePaymentMethod | null> => {
    if (!stripe) {
      setError('Stripe is not initialized');
      return null;
    }

    setLoading(true);
    setError(null);

    try {
        
      const { paymentMethod: newPaymentMethod, error: stripeError } = await stripe.createPaymentMethod({
        type: paymentMethodData.type,
        card: paymentMethodData.card,
        billing_details: paymentMethodData.billing_details,
      });

      if (stripeError) {
        setError(stripeError.message || 'An error occurred');
        return null;
      }

      if (newPaymentMethod) {
        setPaymentMethod(newPaymentMethod as StripePaymentMethod);
        return newPaymentMethod as StripePaymentMethod;
      }

      return null;
    } catch (err) {
      const errorMessage = err instanceof Error ? err.message : 'An unknown error occurred';
      setError(errorMessage);
      return null;
    } finally {
      setLoading(false);
    }
  };

  return {
    createPaymentMethod,
    loading,
    error,
    paymentMethod,
  };
};

export default usePaymentMethod;